import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { StatusDot } from 'common/components/StatusDot';
import { Tooltip } from 'common/components/Tooltip';
import { formatDate } from 'common/utils/formatDate';
import { formatActivityDuration } from 'dashboard/utils';

// Consider users online if their session ended within the last 5 minutes
const ONLINE_THRESHOLD_SECONDS = 300;

interface IActivity {
    startedAt?: string | null;
    endedAt?: string | null;
}

interface IProps {
    activity?: IActivity | null;
    showDuration?: boolean;
    showTooltip?: boolean;
}

/**
 * Displays a user's last activity as a relative time label (e.g. "3 min. ago").
 * Shows a green dot with "Online" when the session ended within the last 5 minutes.
 * Optionally shows session duration and an absolute date tooltip.
 */
export const LastActiveLabel = ({
    activity,
    showDuration,
    showTooltip,
}: IProps) => {
    const [translate] = useTranslation();

    const endDate = activity?.endedAt;

    if (!endDate) return <>-</>;

    const endDateTime = DateTime.fromISO(endDate);
    const startDateTime = activity?.startedAt
        ? DateTime.fromISO(activity.startedAt)
        : null;

    if (!endDateTime.isValid) return <>-</>;

    const secondsSinceActive = -endDateTime.diffNow().as('seconds');
    const isOnline = secondsSinceActive < ONLINE_THRESHOLD_SECONDS;

    const relativeText = isOnline
        ? translate('online')
        : (endDateTime.toRelative({ style: 'narrow' }) ?? '');

    const durationSeconds =
        showDuration && startDateTime?.isValid
            ? endDateTime.diff(startDateTime).as('seconds')
            : null;

    const label = (
        <>
            {relativeText}
            {showDuration &&
                durationSeconds != null &&
                ` · ${formatActivityDuration(durationSeconds)}`}
        </>
    );

    const content = isOnline ? (
        <StatusDot color="success" sx={{ gap: 0.5 }}>
            {label}
        </StatusDot>
    ) : (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
            {label}
        </Box>
    );

    // Show absolute date tooltip for offline users (online status is self-explanatory)
    if (showTooltip && !isOnline) {
        return (
            <Tooltip title={formatDate(endDate, 'd LLL yyyy HH:mm') ?? ''}>
                {content}
            </Tooltip>
        );
    }

    return content;
};
